import { createSlice, Draft, PayloadAction } from "@reduxjs/toolkit";
import { createMultiOptions } from "../../../../utils/createOptions";

interface IClientRegionsList {
  active: boolean;
  areas: { active: boolean; area: { id: number, name: string } }[];
  region: { id: number, name: string };
}

const initialState: any = {
  customers: null,
  customersCount: null,
  limit: JSON.parse(localStorage.getItem("pageLimit") as string) || 10,
  pages: 0,
  customerInputValues: {
    login: null,
    contracts: [],
  },
};

export const customerListSlice = createSlice({
  name: "customerList",
  initialState,
  reducers: {
    reducerCustomerList: (state: Draft<any>, action: PayloadAction<any>) => {
      state.customers = action.payload.data || [];
      state.customersCount = action.payload.count;
      state.limit = action.payload.limit;
    },
    reducerEmployeesInputValues: (
      state: Draft<any>,
      action: PayloadAction<any>
    ) => {
      state.customerInputValues = action.payload;
    },
    reducerClientsStatus: (state: Draft<any>, action: PayloadAction<any>) => {
      state.clientsStatus = action.payload;
    },
    reducerClientsFilters: (state: Draft<any>, action: PayloadAction<any>) => {
      state.clientsFilters = action.payload;
      state.contractsOptions = createMultiOptions(action.payload?.contracts);
    },
    reducerClientContracts: (state: Draft<any>, action: PayloadAction<any>) => {
      state.clientContractsList = action.payload.sort((a: { active: boolean; contract: { id: number; name: string } }, b: { active: boolean; contract: { id: number; name: string } }) =>
        a.contract.name.localeCompare(b.contract.name)
      );
      state.clientContractsLength = action.payload?.length;
      state.allContractsForUpdate = action.payload?.map((elem: { active: boolean; contract: { id: number; name: string } }) => {
        return {
          id: elem.contract.id,
        };
      });
      state.activeContracts = action.payload
        ?.filter((elem: { active: boolean; contract: { id: number; name: string } }) => elem.active)
        ?.map((contract: { active: boolean; contract: { id: number; name: string } }) => contract.contract.id);
    },
    reducerClientRegions: (state: Draft<any>, action: PayloadAction<any>) => {
      state.clientRegionsList = action.payload.sort((a: IClientRegionsList, b: IClientRegionsList) =>
        a.region.name.localeCompare(b.region.name)
      );
      state.clientRegionsLength = action.payload?.length;
      state.activeRegions = action.payload
        ?.filter((elem: IClientRegionsList) => elem.active)
        ?.map((region: IClientRegionsList) => region.region.id);
      state.allRegionsForUpdate = action.payload?.map((elem: IClientRegionsList) => ({
        region: {
          id: elem.region.id,
        },
        areas: elem.areas.map((obj: { active: boolean; area: { id: number, name: string } }) => ({
          area: {
            id: obj.area.id,
          },
        })),
      }));
    },
    reducerClientSettings: (state: Draft<any>, action: PayloadAction<any>) => {
      state.clientSettings = action.payload;
      state.settingsForUpdate = {
        ...action.payload,
        areas: action.payload.areas.map((elem: { active: boolean; area: { id: number, name: string } }) => ({
          area: { id: elem.area.id },
        })),
        contracts: action.payload.contracts.map((elem: {
          active: boolean,
          contract: { id: number, name: string }
        }) => ({
          contract: {
            id: elem.contract.id,
          },
        })),
        reviewStatuses: {
          value: action.payload.reviewStatuses.value?.map((elem: {[key: string]: string}) =>
            Object.keys(elem).toString()
          ),
        },
        regions: action.payload.regions.map((elem: IClientRegionsList) => ({
          region: {
            id: elem.region.id,
          },
          areas: elem.areas.map((area: { active: boolean; area: { id: number, name: string } }) => ({
            area: {
              id: area.area.id,
            },
          })),
        })),
      };
    },
    reducerClientFilters: (state: Draft<any>, action: PayloadAction<any>) => {
      state.declineEndPeriodsOptions = Object.entries(
        action.payload.formData.declineEndPeriods
      ).map(([key, value]) => ({
        value: parseInt(key),
        label: value,
      }));

      state.selectedEndPeriodOption = state.declineEndPeriodsOptions.find(
        (option: any) =>
          option.value === action.payload.settings.declinePeriodEnd.value
      );

      state.declineStartPeriodsOptions = Object.entries(
        action.payload.formData.declineStartPeriods
      ).map(([key, value]) => ({
        value: parseInt(key),
        label: value,
      }));

      state.selectedStartPeriodOption = state.declineStartPeriodsOptions.find(
        (option: any) =>
          option.value === action.payload.settings.declinePeriodStart.value
      );

      state.reportDelaysOptions = Object.entries(
        action.payload.formData.reportDelays
      ).map(([key, value]) => ({
        value: parseInt(key),
        label: value,
      }));

      state.selectedReportDelayOption = state.reportDelaysOptions.find(
        (option: any) =>
          option.value === action.payload.settings.reportsDelay.value
      );
      state.showReportCostPercent = action.payload.settings.showReportCostPercent;
      state.showContractInfo = action.payload.settings.showContractInfo;
      state.analyticsHideRubles = action.payload.settings.analyticsHideRubles
      state.fixHours = action.payload.settings.fixHours;
      state.geo = action.payload.settings.geo;
      state.regionRestriction = action.payload.settings.regionRestriction;
      state.visitSchedule = action.payload.settings.visitSchedule;
      state.reviewStatus = action.payload.settings.reviewStatus;
      state.reviewHistory = action.payload.settings.reviewHistory;
      state.onlyPhotoAndGeo = action.payload.settings.onlyPhotoAndGeo;
      state.fixByNextVisit = action.payload.settings.fixByNextVisit;
      state.hidePhotoGeo = action.payload.settings.hidePhotoGeo;
      state.showReportCheckTypes = action.payload.settings.showReportCheckTypes;
      state.reviewer = action.payload.settings.reviewer;
      state.multiReviewReason = action.payload.settings.multiReviewReason;
      state.periodOsa = action.payload.settings.periodOsa;
      state.averageFacing = action.payload.settings.averageFacing;
      state.shareOfShelf = action.payload.settings.shareOfShelf;
      state.hideTraineeReports = action.payload.settings.hideTraineeReports;
      state.posMap = action.payload.settings.posMap;
      state.analytics = action.payload.settings.analytics;
      state.acceptReviewReasons = action.payload.settings.acceptReviewReasons;
      state.reviewStatusesValue =
        action.payload.settings.reviewStatuses.value?.map((elem: {[key: string]: string}) =>
          Object.keys(elem).toString()
        );
    },
    reducerCustomerClearInputValues: () => {
      return initialState;
    },
    reducerSelectedEmployeesId: (
      state: Draft<any>,
      action: PayloadAction<any>
    ) => {
      state.selectedEmployeesId = action.payload;
    },
  },
});

export const {
  reducerEmployeesInputValues,
  reducerClientContracts,
  reducerClientsFilters,
  reducerClientSettings,
  reducerClientFilters,
  reducerClientsStatus,
  reducerClientRegions,
  reducerCustomerList,
  reducerSelectedEmployeesId,
  reducerCustomerClearInputValues,
} = customerListSlice.actions;

export default customerListSlice.reducer;
